import { MouseEvent, useState } from 'react';

import AddchartIcon from '@mui/icons-material/Addchart';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import ToggleOffOutlinedIcon from '@mui/icons-material/ToggleOffOutlined';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { grey } from '@mui/material/colors';
import { useTheme } from '@mui/system';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import dayjs from 'src/helpers/dayjs';
import { useTranslation } from 'react-i18next';

import CompetitionsList, {
  CompetitionsListEditionProvider,
} from 'src/common/components/CompetitionsList';
import Loader from 'src/common/components/Loader';
import TicketCard from 'src/common/components/styled/TicketCard';
import { LanguageKeys } from 'src/common/constants/languages';
import { IEdition } from 'src/interfaces/Edition';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

interface Props {
  activeEditionId?: string;
  edition: IEdition;
  isLoading?: boolean;
  onDeleteEdition: (editionId: string, e?: MouseEvent<unknown>) => void;
  onEditEdition: (editionId: string, e?: MouseEvent<unknown>) => void;
  onSaveEdition: (editedEdition: IEdition, id?: string) => void;
  onSaveStatistics: (editionId: string, e?: MouseEvent<unknown>) => void;
  onToggleActiveEdition: (editionId: string, e?: MouseEvent<unknown>) => void;
}

export default function EditionCard({
  activeEditionId,
  edition,
  edition: { canShowResults, date, id, signupDeadline, isSignupOpen, year },
  isLoading,
  onDeleteEdition,
  onEditEdition,
  onSaveEdition,
  onSaveStatistics,
  onToggleActiveEdition,
}: Props) {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;
  const { palette } = useTheme();
  const toolsMenu = usePopupState({ variant: 'popover', popupId: `edition-tools-${id}` });

  const { dateFormat, dateTimeFormat } = useSystemOptionsStore(
    systemOptionsState => systemOptionsState,
  );

  const [competitionsOpen, setCompetitionsOpen] = useState(false);

  const isActive = id === activeEditionId;
  const labelColor = isActive ? 'primary.contrastText' : grey[600];
  const bodyColor = isActive ? 'primary.contrastText' : 'text.primary';

  const handleToggleCanShowResults = () =>
    onSaveEdition(
      {
        ...edition,
        canShowResults: !canShowResults,
      },
      id,
    );

  const handleToggleSignupsStatus = () =>
    onSaveEdition(
      {
        ...edition,
        isSignupOpen: !isSignupOpen,
      },
      id,
    );

  const toggleCompetitionsOpen = () => {
    setCompetitionsOpen(!competitionsOpen);
  };

  const closeMenu = () => toolsMenu.close();

  return (
    <TicketCard
      inverted={isActive}
      eyebrow={`${t('editions.edition')} ${year}`}
      avatar={
        <Avatar
          sx={{
            background: `linear-gradient(135deg, ${
              isActive ? palette.success.dark : palette.warning.dark
            } 0%, ${isActive ? palette.success.light : palette.warning.light} 100%)`,
          }}
          aria-label={String(year)}
        >
          {year}
        </Avatar>
      }
      badges={[
        {
          label: isSignupOpen ? t('editions.signupOpen') : t('editions.signupsClosed'),
          highlighted: true,
        },
      ]}
      footer={
        <>
          <IconButton
            {...bindTrigger(toolsMenu)}
            aria-label={t('editions.optionsLabel')}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu {...bindMenu(toolsMenu)}>
            <MenuItem
              onClick={e => {
                onEditEdition(id, e);
                closeMenu();
              }}
            >
              <ListItemIcon>
                <EditIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('editions.editEdition')}</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={e => {
                onDeleteEdition(id, e);
                closeMenu();
              }}
            >
              <ListItemIcon>
                <DeleteForeverIcon color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('editions.deleteEdition')}</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={e => {
                onToggleActiveEdition(id, e);
                closeMenu();
              }}
            >
              <ListItemIcon>
                {isActive ? (
                  <ToggleOnIcon color="primary" fontSize="small" />
                ) : (
                  <ToggleOffOutlinedIcon color="warning" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>
                {isActive ? t('editions.deactivateEdition') : t('editions.activateEdition')}
              </ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleToggleCanShowResults();
                closeMenu();
              }}
            >
              <ListItemIcon>
                {canShowResults ? (
                  <AnalyticsIcon color="primary" fontSize="small" />
                ) : (
                  <AnalyticsOutlinedIcon color="warning" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>
                {canShowResults ? t('editions.hideResults') : t('editions.showResults')}
              </ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleToggleSignupsStatus();
                closeMenu();
              }}
            >
              <ListItemIcon>
                <NoteAltOutlinedIcon color={isSignupOpen ? 'primary' : 'warning'} fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {isSignupOpen ? t('editions.closeSignups') : t('editions.openSignups')}
              </ListItemText>
            </MenuItem>
            <MenuItem
              onClick={e => {
                onSaveStatistics(id, e);
                closeMenu();
              }}
            >
              <ListItemIcon>
                <AddchartIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('editions.saveStatistics')}</ListItemText>
            </MenuItem>
          </Menu>
          <Box sx={{ flex: 1 }} />
        </>
      }
    >
      {isLoading ? <Loader isLoading={isLoading} /> : null}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography
            variant="h5"
            component="div"
            sx={{
              mb: 0.75,
              color: bodyColor,
            }}
          >
            {year}
          </Typography>
          <Typography
            variant="caption"
            component="div"
            sx={{
              color: isActive ? 'primary.contrastText' : 'text.secondary',
              opacity: isActive ? 0.9 : 1,
              lineHeight: 1.5,
            }}
          >
            {dayjs(date).format(dateFormat[currentLang])}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="body2" color={labelColor} sx={{ opacity: isActive ? 0.85 : 1 }}>
            {t('editions.signupDeadline')}:
          </Typography>
          <Typography variant="body2" color={bodyColor}>
            {dayjs(signupDeadline).format(dateTimeFormat[currentLang])}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" color={labelColor} sx={{ opacity: isActive ? 0.85 : 1 }}>
              {t('editions.competitions')}:
            </Typography>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={toggleCompetitionsOpen}
              sx={{ color: isActive ? 'primary.contrastText' : undefined }}
            >
              {competitionsOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
          <Collapse in={competitionsOpen} timeout="auto" unmountOnExit>
            <Box sx={{ pt: 1 }}>
              <CompetitionsListEditionProvider editionId={id}>
                <CompetitionsList variant="list" inverted={isActive} includeUnpublished />
              </CompetitionsListEditionProvider>
            </Box>
          </Collapse>
        </Grid>
      </Grid>
    </TicketCard>
  );
}
